import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Pagination } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiPlus, HiTrash, HiPencil, HiUserGroup } from 'react-icons/hi2';
import { useDateFormat } from '@/utils/dates';
import { PageContent, PageHeader } from '@/components/layout/PageLayout';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import CustomerFormModal from '@/components/customers/CustomerFormModal';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import {
  EmptyState, EmptyStateIcon, EmptyStateTitle, EmptyStateDescription,
  DesktopOnly, MobileOnly, MobileCardList, MobileCard, MobileCardHeader, MobileCardBody, MobileCardActions, MobileCardMain, MobileCardTitle,
  FilterBar,
} from '@/styles/shared.styled';
import {
  useListCustomersQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
} from '@/store/customersApi';
import type { CustomerDto, CreateCustomerRequest } from '@/types';

const PAGE_SIZE = 20;

export default function CustomersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomerDto | null>(null);

  const { data, isLoading } = useListCustomersQuery({ page, pageSize: PAGE_SIZE, search: search || undefined });
  const [createCustomer] = useCreateCustomerMutation();
  const [updateCustomer] = useUpdateCustomerMutation();
  const [deleteCustomer] = useDeleteCustomerMutation();

  const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;

  const openCreate = () => {
    setEditingCustomer(null);
    setShowModal(true);
  };

  const openEdit = (c: CustomerDto) => {
    setEditingCustomer(c);
    setShowModal(true);
  };

  const handleSubmit = async (data: CreateCustomerRequest) => {
    if (editingCustomer) {
      await updateCustomer({ id: editingCustomer.id, ...data });
    } else {
      await createCustomer(data);
    }
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteCustomer(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const customerColumns: DataTableColumn<CustomerDto>[] = [
    { key: 'name', header: t('customers.table.name'), render: (c) => c.name },
    { key: 'email', header: t('customers.table.email'), render: (c) => c.email ?? '—' },
    { key: 'phone', header: t('customers.table.phone'), render: (c) => c.phone ?? '—' },
    { key: 'sprawy', header: t('customers.table.sprawy'), render: (c) => c.sprawyCount, align: 'right' },
    { key: 'createdAt', header: t('customers.table.createdAt'), render: (c) => formatDate(c.createdAt) },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (c) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(c)}>
            <HiPencil size="0.875rem" />
          </Button>
          <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(c)}>
            <HiTrash size="0.875rem" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageContent>
      <PageHeader
        title={t('customers.title')}
        subtitle={t('customers.subtitle')}
        actions={
          <Button variant="primary" onClick={openCreate}>
            <HiPlus size="1rem" className="me-2" />
            {t('customers.addCustomer')}
          </Button>
        }
      />

      <FilterBar>
        <Form.Control
          type="text"
          placeholder={t('customers.searchPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </FilterBar>

      {isLoading ? (
        <LoadingSpinner />
      ) : !data?.items.length ? (
        <EmptyState>
          <EmptyStateIcon><HiUserGroup /></EmptyStateIcon>
          <EmptyStateTitle>{t('customers.empty.title')}</EmptyStateTitle>
          <EmptyStateDescription>{t('customers.empty.description')}</EmptyStateDescription>
        </EmptyState>
      ) : (
        <>
          <DesktopOnly>
            <DataTable<CustomerDto>
              ariaLabel={t('customers.title')}
              rowKey={(c) => c.id}
              onRowClick={(c) => navigate(`/customers/${c.id}`)}
              columns={customerColumns}
              rows={data.items}
            />
          </DesktopOnly>

          <MobileOnly>
            <MobileCardList>
              {data.items.map((c) => (
                <MobileCard key={c.id} $clickable onClick={() => navigate(`/customers/${c.id}`)}>
                  <MobileCardHeader>
                    <MobileCardMain>
                      <MobileCardTitle>{c.name}</MobileCardTitle>
                    </MobileCardMain>
                  </MobileCardHeader>
                  <MobileCardBody>
                    {c.email && <span>{c.email}</span>}
                    {c.phone && <span>{c.phone}</span>}
                    <span>{c.sprawyCount} {t('customers.table.sprawy')}</span>
                    <span>{formatDate(c.createdAt)}</span>
                  </MobileCardBody>
                  <MobileCardActions onClick={(e) => e.stopPropagation()}>
                    <Button variant="outline-primary" size="sm" onClick={() => openEdit(c)}>
                      <HiPencil size="0.875rem" />
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(c)}>
                      <HiTrash size="0.875rem" />
                    </Button>
                  </MobileCardActions>
                </MobileCard>
              ))}
            </MobileCardList>
          </MobileOnly>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                {t('customers.showing', {
                  from: (page - 1) * PAGE_SIZE + 1,
                  to: Math.min(page * PAGE_SIZE, data.totalCount),
                  total: data.totalCount,
                })}
              </small>
              <Pagination className="mb-0">
                <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
                {Array.from({ length: totalPages }, (_, i) => (
                  <Pagination.Item key={i + 1} active={page === i + 1} onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next disabled={page >= totalPages} onClick={() => setPage(page + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}

      <CustomerFormModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        editing={editingCustomer}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('customers.deleteTitle')}
        message={t('customers.deleteConfirm', { name: deleteTarget?.name })}
        confirmLabel={t('confirm.delete')}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageContent>
  );
}
